/**
 * **Barcode produk** — dipisah dari kartu detail (Imron, 2026-08-03).
 *
 * ⭐ Alasannya bukan kerapian melainkan **formulir**: barcode punya formulirnya SENDIRI (tambah
 * satu baris), dan formulir di dalam formulir tak sah. Karena itu tabnya berada di tingkat LAYAR,
 * bukan di dalam `FormTabs` milik detail produk.
 *
 * ⛔ Tetap TABEL, bukan `Bar Code 1/2/3` ala VTEC: barcode keempat tak boleh mustahil, dan
 * pencarian tak boleh harus memeriksa tiga kolom sekaligus.
 */
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Table, Tag, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Barcode, Product } from '../api/client';
import { canWrite } from '../api/session';

export function ProductBarcodes({ product }: { readonly product: Product }) {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [barcode, setBarcode] = useState<readonly Barcode[]>([]);

  const muatBarcode = useCallback(() => {
    void api.barcodes(product.id).then(setBarcode).catch(() => undefined);
  }, [product.id]);
  useEffect(() => { muatBarcode(); }, [muatBarcode]);

  return (
    <Card size="small" data-testid="produk-barcode">
      {/* ── barcode: TABEL, bukan tiga kolom ──────────────────────────────── */}
      <Alert type="info" showIcon style={{ marginBottom: 8 }} title={t('product.barcodeHint')} />
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={[...barcode]}
        columns={[
          {
            title: t('product.barcodes'),
            dataIndex: 'barcode',
            render: (v: string, r: Barcode) => (
              <span data-testid={`barcode-${v}`}>
                {v} {r.is_primary && <Tag color="green">1</Tag>}
              </span>
            ),
          },
          ...(boleh
            ? [
                {
                  title: '',
                  key: 'aksi',
                  width: 80,
                  render: (_: unknown, r: Barcode) => (
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      data-testid={`barcode-hapus-${r.barcode}`}
                      onClick={() => { void api.deleteBarcode(r.id).then(muatBarcode).catch(() => undefined); }}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      {boleh && (
        <Form
          layout="inline"
          style={{ marginTop: 10 }}
          onFinish={(v: { barcode: string }) => {
            void api
              .createBarcode({ productId: product.id, barcode: v.barcode })
              .then(muatBarcode)
              .catch((e: Error) => { void message.error(e.message); });
          }}
        >
          <Form.Item name="barcode" rules={[{ required: true }]}>
            <Input placeholder={t('product.barcodes')} data-testid="barcode-nilai" />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" icon={<PlusOutlined />} data-testid="barcode-tambah">
              {t('product.barcodeAdd')}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Card>
  );
}
